/**
 * Đổi lỗi API thành câu tiếng Việt cho các trang xác thực.
 *
 * utils/axios-utils.ts ném ra `error.response.data`, tức `{ statusCode, message }` của NestJS
 * (message có thể là mảng khi class-validator từ chối DTO). Mất mạng / CORS thì ném
 * `{ message, networkError: true }` (không có statusCode). Proxy trả HTML thì ném chuỗi.
 */

/** Chuỗi `message` backend trả về đúng từng chữ (so khớp không phân biệt hoa thường). */
export const SERVER_MESSAGES = {
  INVALID_CREDENTIALS: 'Invalid Credentials', // 401 login: email không có
  UNAUTHORIZED: 'Unauthorized', // 401 login: sai mật khẩu
  NOT_VERIFIED: 'This user account not verify', // 401 login
  DISABLED: 'This user account has been disabled', // 401 login
  CODE_WRONG: 'Code Wrong with Email', // 400 users/verify, auth/verify/code-forgot, auth/update-pass/forgot
  USER_NOT_VERIFY: 'User not verify', // 400 auth/forgot-password: email không có / chưa xác minh
  USER_EXISTS: 'User already exists', // 409 auth/register (email) và users/check (TikTok ID)
  TIKTOK_EXISTS: 'Tiktok ID already exists', // 409 auth/register
  CODE_REQUIRED_WRONG: 'CODE_WRONG', // 400 auth/register khi gửi "Mã HP Media" sai (admin không gửi)
} as const;

export const AUTH_MESSAGES = {
  network: 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.',
  rateLimited: 'Bạn thử quá nhiều lần. Vui lòng đợi khoảng 20 giây rồi thử lại.',
  server: 'Hệ thống đang bận. Vui lòng thử lại sau ít phút.',
  /** Dùng cho auth/register và auth/forgot-password: 5xx gần như luôn do gửi email lỗi. */
  emailSendFailed: 'Chưa gửi được email xác thực. Vui lòng thử lại sau hoặc liên hệ Admin.',
  invalidInput: 'Thông tin chưa hợp lệ. Vui lòng kiểm tra lại các ô đã nhập.',
  fallback: 'Có lỗi xảy ra. Vui lòng thử lại.',
} as const;

export type AuthErrorKind = 'network' | 'rateLimited' | 'server' | 'client';

export interface AuthErrorInfo {
  kind: AuthErrorKind;
  /** HTTP status nếu biết (không có khi mất mạng). */
  status?: number;
  /** `message` gốc của backend, đã gộp mảng thành một chuỗi. */
  serverMessage: string;
}

const NETWORK_MESSAGE = 'Không kết nối được máy chủ.';

/** Đọc lỗi axios (đã qua interceptor) thành dạng có cấu trúc. Không bao giờ ném. */
export function readAuthError(error: unknown): AuthErrorInfo {
  if (typeof error === 'string') {
    // Proxy / máy chủ trả trang HTML: coi như lỗi máy chủ.
    return { kind: 'server', serverMessage: error.slice(0, 200) };
  }
  const data = (error && typeof error === 'object' ? error : {}) as Record<string, any>;
  const rawMessage = data.message;
  const serverMessage = Array.isArray(rawMessage)
    ? rawMessage.join('; ')
    : typeof rawMessage === 'string'
    ? rawMessage
    : '';
  const status = Number(data.statusCode ?? data.status ?? data.code) || undefined;

  if (!status) {
    if (data.networkError || serverMessage === NETWORK_MESSAGE || !serverMessage) {
      return { kind: 'network', serverMessage };
    }
    return { kind: 'client', serverMessage };
  }
  if (status === 429) return { kind: 'rateLimited', status, serverMessage };
  if (status >= 500) return { kind: 'server', status, serverMessage };
  return { kind: 'client', status, serverMessage };
}

export interface AuthErrorOverrides {
  /** Khớp theo `message` backend (không phân biệt hoa thường). Ưu tiên cao nhất. */
  byMessage?: Record<string, string>;
  /** Khớp theo HTTP status (vd. { 409: '...' }). */
  byStatus?: Record<number, string>;
  network?: string;
  rateLimited?: string;
  /** Câu cho mọi lỗi 5xx. */
  server?: string;
  /** Câu cho lỗi 4xx không khớp gì ở trên. Mặc định: 400 → invalidInput, còn lại → fallback. */
  fallback?: string;
}

/**
 * Trả về MỘT câu tiếng Việt để hiện trong <Alert>. Thứ tự: byMessage → byStatus →
 * mất mạng / 429 / 5xx → fallback.
 *
 * @example
 * mapAuthError(err, {
 *   byStatus: { 409: 'Email này đã có tài khoản. Hãy đăng nhập hoặc dùng Quên mật khẩu.' },
 *   server: AUTH_MESSAGES.emailSendFailed,
 * });
 */
export function mapAuthError(error: unknown, overrides: AuthErrorOverrides = {}): string {
  const info = readAuthError(error);
  const wanted = info.serverMessage.trim().toLowerCase();
  if (wanted && overrides.byMessage) {
    const hit = Object.keys(overrides.byMessage).find((key) => key.toLowerCase() === wanted);
    if (hit) return overrides.byMessage[hit];
  }
  if (info.status && overrides.byStatus?.[info.status]) return overrides.byStatus[info.status];

  switch (info.kind) {
    case 'network':
      return overrides.network ?? AUTH_MESSAGES.network;
    case 'rateLimited':
      return overrides.rateLimited ?? AUTH_MESSAGES.rateLimited;
    case 'server':
      return overrides.server ?? AUTH_MESSAGES.server;
    default:
      if (overrides.fallback) return overrides.fallback;
      return info.status === 400 ? AUTH_MESSAGES.invalidInput : AUTH_MESSAGES.fallback;
  }
}

/** Tiện cho `if (isServerMessage(err, SERVER_MESSAGES.CODE_WRONG)) form.setFields(...)`. */
export function isServerMessage(error: unknown, message: string): boolean {
  return readAuthError(error).serverMessage.trim().toLowerCase() === message.toLowerCase();
}
